import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import './events.web-sso';
import { useSsoExchangeToken } from '@zep/apis';
import { useAuth } from '@zep/auth';
import { trackEvent } from '@zep/logger/tracking';
import { buildAuthAppSigninUrl, getErrorHttpStatus } from '../../utils/auth';
import { clearSsoAutoSignin, consumeSsoAutoSignin } from './ssoAutoSignin';
import { useSsoErrorDialog } from './useSsoErrorDialog';
const DEFAULT_CALLBACK_URL = '/home/spaces';

/**
 * callbackUrl이 안전한 상대 경로인지 검증
 */
function getSafeRedirectUrl(url: string | undefined | null, fallbackUrl: string): string {
  if (!url) return fallbackUrl;
  // 상대 경로(/로 시작)만 허용, protocol-relative(//로 시작) 차단
  return url.startsWith('/') && !url.startsWith('//') ? url : fallbackUrl;
}

/**
 * SSO Exchange Token Callback View
 *
 * Auth Server에서 발급한 토큰을 ZEP 서버 인증으로 교환하는 페이지
 *
 * 토큰 전달 방식:
 * - Cross-domain: URL query parameter로 토큰 전달 (?token=xxx)
 * - Same-domain: 쿠키로 자동 전달
 *
 * 성공 시 callbackUrl 또는 /home/spaces로 리다이렉트
 */
export const SsoExchangeTokenCallbackPageView = () => {
  const router = useRouter();
  const {
    login
  } = useAuth();
  const {
    handleOpen: handleOpenSsoErrorDialog
  } = useSsoErrorDialog();
  const ssoMethod = typeof router.query.sso_method === 'string' ? router.query.sso_method : undefined;
  const callbackUrl = useMemo(() => {
    const rawCallbackUrl = typeof router.query.callbackUrl === 'string' ? router.query.callbackUrl : undefined;
    return getSafeRedirectUrl(rawCallbackUrl, DEFAULT_CALLBACK_URL);
  }, [router.query.callbackUrl]);

  // 마운트 시 1회 발화하는 콜백형 뮤테이션 — 성공/실패 분기가 useEffect 밖에 있어야 읽기 쉽다.
  // eslint-disable-next-line zep/no-tanstack-mutate
  const {
    mutate: exchangeToken
  } = useSsoExchangeToken({
    onSuccess: async data => {
      const {
        player
      } = data;
      await login(player);
      trackEvent('login_complete', {
        ...(ssoMethod && {
          type: ssoMethod
        })
      });
      clearSsoAutoSignin();
      void router.replace(callbackUrl);
    },
    onError: err => {
      console.error('SSO token exchange failed:', err);
      // 토큰 없이 착지한 401(북마크·뒤로가기·5분 만료)은 에러가 아니라 "로그인 필요".
      // 다이얼로그 없이 auth 로 보내고, 같은 탭 2분 내 재발이면 루프로 보고 다이얼로그.
      const hasUrlToken = typeof router.query.token === 'string';
      if (getErrorHttpStatus(err) === 401 && !hasUrlToken && consumeSsoAutoSignin()) {
        // 발화는 auth-app 도착 시점이 담당한다(`auth_signin_sso_error_view`).
        // 여기서 track 하면 replace 가 비콘보다 빨라 유실된다.
        window.location.replace(buildAuthAppSigninUrl(router.locale ?? 'en', callbackUrl, {
          ssoError: 'exchange_401',
          fromLogin: router.query.from_login === '1',
          ssoMethod
        }));
        return;
      }
      handleOpenSsoErrorDialog(err);
    }
  });
  useEffect(() => {
    if (!router.isReady) return;

    // Cross-domain 케이스: URL query에서 토큰 추출
    const token = router.query.token as string | undefined;

    // 교환 요청의 Referer·히스토리·오류 화면 주소에 토큰이 남지 않게 먼저 지운다.
    // 동기 replaceState 여야 뒤따르는 요청에 반영된다(quiz 교환 페이지와 같은 방식).
    if (token) {
      const urlWithoutToken = new URL(window.location.href);
      urlWithoutToken.searchParams.delete('token');
      window.history.replaceState(window.history.state, '', urlWithoutToken.toString());
    }
    exchangeToken({
      token
    });
  }, [router.isReady, router.query.token, exchangeToken]);
  return null;
};